import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ===========================================================================
// La tanda que el motor da por cierta, para mirarla antes de firmarla.
//
// Desde que NADA se concilia solo, lo que el motor antes aplicaba por su cuenta
// llega aquí como propuesta. Un mes trae doscientas obvias, y firmarlas de una
// en una es media mañana de pulsar «sí» — que acaba en pulsar sin leer, y eso
// es peor que no tener la compuerta: parece una revisión y no lo es.
//
// Por eso son DOS PASOS y no uno. Primero se abre la lista, con el abono, la
// boleta y el porqué de cada línea, para poder recorrerla. Solo después
// aparece el botón que la firma, y firma exactamente las líneas que se vieron
// —se mandan los pares uno a uno, no un criterio— porque quien firma responde
// por la lista que miró, no por una consulta que puede haber cambiado.
// ===========================================================================

type Fila = {
  movimientoId: string;
  comprobanteId: string;
  fecha: string;
  importe: number;
  moneda: "PEN" | "USD";
  descripcion: string | null;
  serieNumero: string | null;
  cliente: string | null;
  totalBoleta: number | null;
  confianza: string;
  puntaje: string;
  motivo: string | null;
};

const SIMBOLO = { PEN: "S/", USD: "US$" } as const;

const dinero = (c: number | null, m: "PEN" | "USD") =>
  c === null
    ? "—"
    : `${SIMBOLO[m]} ${(c / 100).toLocaleString("es-PE", {
        minimumFractionDigits: 2,
      })}`;

export function TandaDelMotor() {
  const [abierta, setAbierta] = useState(false);
  const qc = useQueryClient();
  const confirmar = api.confirmarTanda.useMutation();

  const { data } = api.tandaDelMotor.useQuery(["tanda"], {
    query: { confianza: "exacto", limite: 200 },
  });
  const d = data?.status === 200 ? data.body : null;
  const filas = (d?.filas ?? []) as Fila[];

  // Sin nada que ofrecer, la barra no existe. Un panel que dice «0 propuestas»
  // ocupa el sitio de lo que sí hay que mirar.
  if (!d || filas.length === 0) return null;

  const firmar = () => {
    confirmar.mutate(
      {
        body: {
          pares: filas.map((f) => ({
            movimientoId: f.movimientoId,
            comprobanteId: f.comprobanteId,
          })),
          confirmo: true,
        },
      },
      {
        onSuccess: (r: {
          status: number;
          body: {
            dice?: string;
            message?: string;
            saltados?: Array<{ movimientoId: string; porque: string }>;
          };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo firmar");
            return;
          }
          toast.success(r.body.dice ?? "Firmadas");
          // Los que ya no estaban donde se los dejó se dicen, no se esconden:
          // quien firmó tiene que saber que su lista no era la de ahora.
          if (r.body.saltados?.length) {
            toast.warning(
              `${r.body.saltados.length} no se firmaron: cambiaron mientras mirabas`,
              {
                description: [
                  ...new Set(r.body.saltados.map((s) => s.porque)),
                ].join(" · "),
                duration: 10_000,
              },
            );
          }
          setAbierta(false);
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo firmar"),
      },
    );
  };

  return (
    <div className="rounded-lg border border-cyan-600/40 bg-cyan-500/5">
      <button
        type="button"
        onClick={() => setAbierta((x) => !x)}
        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left"
      >
        <Sparkles className="size-4 shrink-0 text-cyan-700 dark:text-cyan-400" />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-[13px]">
            El motor da por ciertas {filas.length.toLocaleString("es-PE")}
            {d.total > filas.length
              ? ` de ${d.total.toLocaleString("es-PE")}`
              : ""}
          </span>
          <span className="block text-[11.5px] text-muted-foreground">
            Ninguna está conciliada: esperan tu firma. Abrí la lista para
            revisarlas y firmarlas de una vez.
          </span>
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            !abierta && "-rotate-90",
          )}
        />
      </button>

      {abierta ? (
        <div className="border-cyan-600/30 border-t">
          <div className="max-h-[320px] overflow-auto">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-1.5 text-left font-semibold">Fecha</th>
                  <th className="px-3 py-1.5 text-right font-semibold">
                    Abono
                  </th>
                  <th className="px-3 py-1.5 text-left font-semibold">
                    Lo que dice el banco
                  </th>
                  <th className="px-3 py-1.5 text-left font-semibold">
                    Boleta
                  </th>
                  <th className="px-3 py-1.5 text-right font-semibold">
                    Total
                  </th>
                  <th className="px-4 py-1.5 text-left font-semibold">
                    Por qué
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr
                    key={`${f.movimientoId}|${f.comprobanteId}`}
                    className="border-b last:border-0"
                  >
                    <td className="px-4 py-1.5 tabular-nums">{f.fecha}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {dinero(f.importe, f.moneda)}
                    </td>
                    <td
                      className="max-w-[280px] truncate px-3 py-1.5"
                      title={f.descripcion ?? ""}
                    >
                      {f.descripcion ?? "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      <b className="font-medium">{f.serieNumero ?? "—"}</b>
                      <span className="block max-w-[200px] truncate text-[11px] text-muted-foreground">
                        {f.cliente ?? ""}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {dinero(f.totalBoleta, f.moneda)}
                    </td>
                    <td
                      className="max-w-[320px] truncate px-4 py-1.5 text-muted-foreground"
                      title={f.motivo ?? ""}
                    >
                      {f.motivo ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* El botón vive DEBAJO de la lista y no arriba: firmar sin haber
              bajado por ella es exactamente lo que esta pantalla evita. */}
          <div className="flex flex-wrap items-center gap-2 border-cyan-600/30 border-t px-4 py-2.5">
            <span className="text-[11.5px] text-muted-foreground">
              Al firmar quedan conciliadas a tu nombre, y en la bitácora.
            </span>
            <Button
              size="sm"
              className="ml-auto"
              disabled={confirmar.isPending}
              onClick={firmar}
            >
              <Check className="mr-1.5 size-3.5" />
              Confirmar las {filas.length.toLocaleString("es-PE")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
